import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import debounce from "debounce";

export default class extends Controller {
  static targets = ["pane", "saveButton", "blockedHint", "amount", "includeCheckbox", "targetingMode"];
  static values = { previewUrl: String, paramKey: { type: String, default: "general_cost" } };

  #abortController = null;
  #lotAreaAbortController = null;

  initialize() {
    this.refresh = debounce(this.#sendPreview.bind(this), 300);
  }

  connect() {
    this.#syncChrome();
  }

  disconnect() {
    this.refresh.clear();
    this.#abortInFlight();
    this.#lotAreaAbortController?.abort();
  }

  paneTargetConnected() {
    this.#syncChrome();
  }

  amountChanged() {
    this.refresh();
  }

  selectLotBlock(event) {
    event.preventDefault();
    const lotIds = this.#parseLotIds(event.params.lotIds);
    if (lotIds.length === 0) return;

    const selected = new Set(lotIds.map(String));
    this.includeCheckboxTargets.forEach((checkbox) => {
      if (checkbox.disabled || !selected.has(String(checkbox.value)) || checkbox.checked) return;

      checkbox.checked = true;
    });
    this.refresh();
  }

  saveArea(event) {
    event.preventDefault();
    const url = event.params.url;
    if (!url) return;

    const row = event.target.closest("[data-lot-id]");
    const input = row?.querySelector("[data-area-input]");
    const body = this.#allocationFormData();
    body.append("lot[area_hectares]", input?.value ?? "");
    this.#turboStreamRequest(url, body, "PATCH", "lotArea");
  }

  #syncChrome() {
    if (!this.hasPaneTarget) return;

    const blocked = this.paneTarget.dataset.saveBlocked === "true";
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.toggleAttribute("disabled", blocked);
    }
    if (this.hasBlockedHintTarget) {
      const message = this.paneTarget.dataset.blockedMessage || "";
      this.blockedHintTarget.textContent = message;
      this.blockedHintTarget.classList.toggle("hidden", !blocked || message === "");
    }
  }

  #allocationFormData() {
    const body = new FormData();
    const paramKey = this.paramKeyValue;
    const amount = this.hasAmountTarget ? this.amountTarget.value : "";
    const amountField = paramKey === "general_cost_schedule" ? "default_amount" : "amount";
    body.append("allocation_param_key", paramKey);
    body.append(`${paramKey}[${amountField}]`, amount);
    body.append(`${paramKey}[targeting_mode]`, this.#targetingMode());
    body.append(`${paramKey}[allocated_lot_ids][]`, "");
    this.#checkedLotIds().forEach((id) => {
      body.append(`${paramKey}[allocated_lot_ids][]`, id);
    });
    return body;
  }

  #targetingMode() {
    const selected = this.targetingModeTargets.find((input) => input.checked);
    return selected?.value || "all_lots";
  }

  #checkedLotIds() {
    return this.includeCheckboxTargets
      .filter((checkbox) => checkbox.checked && !checkbox.disabled)
      .map((checkbox) => checkbox.value);
  }

  #parseLotIds(value) {
    if (Array.isArray(value)) return value;
    if (typeof value !== "string" || value === "") return [];

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  async #turboStreamRequest(url, body, method, channel) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;
    const abortController = new AbortController();
    if (channel === "lotArea") {
      this.#lotAreaAbortController?.abort();
      this.#lotAreaAbortController = abortController;
    } else {
      this.#abortInFlight();
      this.#abortController = abortController;
    }

    try {
      const response = await fetch(url, {
        method,
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken,
        },
        body,
        signal: abortController.signal,
      });
      if (!response.ok && response.status !== 422) return;

      await Turbo.renderStreamMessage(await response.text());
    } catch (error) {
      if (error?.name === "AbortError") return;
      throw error;
    }
  }

  #sendPreview() {
    if (!this.previewUrlValue) return;

    this.#turboStreamRequest(this.previewUrlValue, this.#allocationFormData(), "POST", "preview");
  }

  #abortInFlight() {
    this.#abortController?.abort();
  }
}
